// Screen Wake Lock for the standalone lightshow client.
//
// A 3-minute show would be ruined if phones auto-lock/dim, so we hold a screen
// wake lock the whole time the viewer is on the page. The OS releases the lock
// when the tab is hidden, so we re-acquire on visibilitychange. Unsupported
// browsers (e.g. iOS < 16.4) silently no-op — the wall instructions still tell
// users to keep their screen on.
//
// `releaseCount` counts INVOLUNTARY releases (OS dropped the lock — i.e. the
// screen went away) for screen-awake telemetry; our own release() is excluded.

let sentinel = null;
let active = false;
let osReleaseCount = 0;
let visHandler = null;

const supported = () => typeof navigator !== 'undefined' && 'wakeLock' in navigator;

function onRelease() {
  // Our release() removes this listener first, so this only fires when the OS
  // drops the lock (tab hidden / system) = the screen actually went away.
  active = false;
  osReleaseCount++;
}

async function request() {
  if (!supported() || active) return;
  try {
    sentinel = await navigator.wakeLock.request('screen');
    active = true;
    sentinel.addEventListener('release', onRelease);
    if (!visHandler) {
      visHandler = () => { if (document.visibilityState === 'visible') request(); };
      document.addEventListener('visibilitychange', visHandler);
    }
  } catch {
    // Needs a user gesture on some browsers, or unsupported — caller retries on tap.
    active = false;
    sentinel = null;
  }
}

async function release() {
  try {
    if (sentinel) {
      sentinel.removeEventListener('release', onRelease); // don't count our own release
      await sentinel.release();
    }
  } catch { /* ignore */ }
  sentinel = null;
  active = false;
  if (visHandler) { document.removeEventListener('visibilitychange', visHandler); visHandler = null; }
}

export const wakeLock = {
  request,
  release,
  isSupported: supported,
  get isActive() { return active; },
  get releaseCount() { return osReleaseCount; },
};
